import { createContext } from 'react';

import type checkFieldValidity from '@/helpers/questionnaires/checkFieldValidity';
import { type Agent, type Creditor } from '@/typings/api-models';
import type { Questionnaire } from '@/typings/Questionnaire';
import type * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';

import { type BankruptcyExemption } from '../form/List/getDisplayValue';

export type ActiveUser = {
  id: string;
  name: string | null;
  logo?: string | null;
  color?: string | null;
  sectionId: string | null;
  fieldTemplateKey: string | null;
  sessionId: string;
  lastSeen: number; // Timestamp of last activity
  removalReason?: 'timeout' | 'explicit_exit' | 'manual'; // Why user was/should be removed
};

export type FieldResponseMetadata = {
  updatedAt: string;
  submittedById: string | null;
};

export type QuestionnairePerson = {
  id: string;
  name: string | null;
  url: string | null;
  color: string | null;
};

export type QuestionnaireContextType = {
  activeUsers: ActiveUser[];
  setActiveUsers: (users: ActiveUser[]) => void;
  agents: Agent[] | null;
  agentsReadyToRun: boolean;
  checkFieldValidity: typeof checkFieldValidity;
  currentSection: string;
  defaultValues: Record<string, unknown>;
  formRequestId: string | null;
  referenceData: QuestionnaireApiModels.DataReference[];
  setCurrentSection: (sectionId: string) => void;
  getSectionProgress: (sectionId: string, fieldsVisibility: Record<string, boolean>) => number;
  sectionVisibility: Record<string, boolean>;
  setSectionVisibility: (sectionId: string, isVisible: boolean) => void;
  isEnabled: boolean;
  setIsEnabled: (isEnabled: boolean) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
  newListItemId: string | null;
  setNewListItemId: (id: string | null) => void;
  setReferenceData: (data: QuestionnaireApiModels.DataReference[]) => void;
  getSubsectionProgress: (
    sectionId: string,
    subsection: string,
    fieldsVisibility: Record<string, boolean>,
  ) => {
    completedFields: number;
    subsectionProgressPercentage: number | null;
    totalFields: number;
  } | null;
  sectionErrors: { [key: string]: number };
  setSectionErrors: (errors: { [key: string]: number }) => void;
  onSubmit: (responses: QuestionnaireApiModels.UpdateResponseData, isCompleted?: boolean) => Promise<boolean>;
  aiAutofillActions: string[];
  fieldsByKey: Record<string, Questionnaire.Field>;
  fieldsById: Record<string, Questionnaire.Field>;
  formData: Questionnaire.Questionnaire;
  userWorkflowId: string | null;
  questionnaireId: string;
  questionnaireTemplateId: string;
  questionnaireResponses: Array<
    Pick<
      QuestionnaireApiModels.QuestionnaireResponse,
      | 'id'
      | 'associatedWithId'
      | 'index'
      | 'value'
      | 'visible'
      | 'aiRequestId'
      | 'fieldTemplateId'
      | 'source'
      | 'sourceId'
      | 'sourceIndex'
      | 'submittedById'
      | 'listRowResponseId'
      | 'questionnaireId'
      | 'correlationId'
      | 'hasAutofillRun'
      | 'explanationHtml'
      | 'createdAt'
      | 'updatedAt'
      | 'isRemoved'
    >
  >;
  refetchCreditors: () => void;
  creditors: Creditor[];
  courtDivisionsDataset: { table: Array<{ key: string; label: string; stateOfFilingFilter: string }> } | null;
  federalExemptionsDataset: { table: BankruptcyExemption[] } | null;
  stateExemptionsDataset: { table: BankruptcyExemption[] } | null;
  dataReferencesBySlug: QuestionnaireApiModels.DataReference | null;
  fieldTemplatesMap: Map<
    string,
    Pick<
      QuestionnaireApiModels.FieldTemplate,
      'id' | 'key' | 'listFieldTemplateId' | 'type' | 'options' | 'conditionExpression' | 'tableColumns'
    >
  >;
  pdfTemplates: QuestionnaireApiModels.PdfTemplate[];
  pdfFillJsonsBySection: Record<string, Record<string, unknown>>;
  pdfFills: Array<
    QuestionnaireApiModels.PdfFill & {
      expression: Omit<QuestionnaireApiModels.Expression, 'variables'> | null;
      pdfFieldTemplate: QuestionnaireApiModels.PdfFieldTemplate & {
        pdfTemplate: QuestionnaireApiModels.PdfTemplate;
      };
      listFieldTemplate: Pick<QuestionnaireApiModels.FieldTemplate, 'id' | 'listFilterExpression'> | null;
    }
  >;
  setPdfFills: (
    pdfFills: Array<
      QuestionnaireApiModels.PdfFill & {
        expression: Omit<QuestionnaireApiModels.Expression, 'variables'> | null;
        pdfFieldTemplate: QuestionnaireApiModels.PdfFieldTemplate & {
          pdfTemplate: QuestionnaireApiModels.PdfTemplate;
        };
        listFieldTemplate: Pick<QuestionnaireApiModels.FieldTemplate, 'id' | 'listFilterExpression'> | null;
      }
    >,
  ) => void;
  userWorkflowContext: Record<string, unknown>;
  setUserWorkflowContext: (context: Record<string, unknown>) => void;
  isSavingResponse: boolean;
  setIsSavingResponse: (isSaving: boolean) => void;
  getFieldResponseMetadata: (fieldKey: string) => FieldResponseMetadata | null;
  updateFieldResponseMetadata: (fieldKey: string, metadata: FieldResponseMetadata) => void;
  questionnairePeople: Record<string, QuestionnairePerson>;
  getQuestionnairePerson: (personId: string) => Promise<QuestionnairePerson | null>;
  logger: {
    error: (message: string, metadata?: Record<string, unknown>) => void;
    warn: (message: string, metadata?: Record<string, unknown>) => void;
    info: (message: string, metadata?: Record<string, unknown>) => void;
  };
};

const QuestionnaireContext = createContext<QuestionnaireContextType | undefined>(undefined);

export default QuestionnaireContext;
